import { CONFIG } from '~/_shared/constants/config';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { createStateSelector } from '~/_shared/utils/memoize/createSelector';
import {
  extraFillConfigPerBTGIdSelector, extraFillInfoPerFillIdSelector,
} from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/boundaryExtraFillSelectors';
import {
  type BoundaryId, type BoundaryTerritoryGroupId, type BoundaryTerritoryId,
} from '~/store/boundaries/boundaryIdentifier.type';
import { type AppState } from '../app.store';

export const boundaryTerritoryDetailsFilterHashSelector = (state: AppState) => state.map.boundaryTerritoryDetails.territoryDetails.filterHash;

const boundaryTerritoryDetailsSelector = (state: AppState) => state.map.boundaryTerritoryDetails.territoryDetails.data;
export const useBoundaryTerritoryDetailsSelector = () => useSelector(boundaryTerritoryDetailsSelector);

const boundaryTerritoryDetailsIsLoadingSelector = (state: AppState) => state.map.boundaryTerritoryDetails.isLoading;
export const useBoundaryTerritoryDetailsIsLoadingSelector = () => useSelector(boundaryTerritoryDetailsIsLoadingSelector);

export type BoundaryTerritoryAssignment = {
  boundaryTerritoryGroupId: BoundaryTerritoryGroupId;
  territoryAssignments: ReadonlyMap<BoundaryTerritoryId, ReadonlySet<BoundaryId>>;
  boundaryAssignments: ReadonlyMap<BoundaryId, BoundaryTerritoryId>;
};

export const boundaryTerritoryAssignmentsSelector = createStateSelector(
  boundaryTerritoryDetailsSelector,
  extraFillConfigPerBTGIdSelector,
  extraFillInfoPerFillIdSelector,
  (details, extraFillDataPerBtgId, extraFillDetailsPerFillId) => {
    const boundaryTerritoryGroupIds = Array.from(details.keys());

    return new Map(boundaryTerritoryGroupIds.map(BTGId => {
      const activeExtraFillId = extraFillDataPerBtgId[BTGId]?.activeBoundaryFillId;
      const activeExtraFillInfo = activeExtraFillId ? extraFillDetailsPerFillId[activeExtraFillId] : null;
      const activeFillDetails = CONFIG.ENABLE_ALT_BOUNDARY_FILLS ?
        activeExtraFillInfo?.btgDetails?.boundaryDetails :
        null;

      const territoryAssignments: Map<BoundaryTerritoryId, Set<BoundaryId>> = new Map();
      const boundaryAssignments: Map<BoundaryId, BoundaryTerritoryId> = new Map();

      const itemDetails = activeFillDetails || details.get(BTGId);
      const generatedAssignments = itemDetails?.assignments ?? {};
      const manualAssignments = itemDetails?.manualAssignments ?? {};

      // Manual assignments
      for (const [territoryId, assignments] of Object.entries(manualAssignments)) {
        const territoryBoundaries = new Set<number>();

        for (const boundaryId of assignments) {
          territoryBoundaries.add(boundaryId);
          boundaryAssignments.set(boundaryId, territoryId);
        }

        territoryAssignments.set(territoryId, territoryBoundaries);
      }

      // Generated assignments
      for (const [territoryId, assignments] of Object.entries(generatedAssignments)) {
        const territoryBoundaries = territoryAssignments.get(territoryId) ?? new Set<number>();

        for (const boundaryId of assignments) {

          // Manual assignments take precedence.
          // If an assignment already exists, do not process the generated assignment.
          if (!boundaryAssignments.has(boundaryId)) {
            territoryBoundaries.add(boundaryId);
            boundaryAssignments.set(boundaryId, territoryId);
          }
        }

        territoryAssignments.set(territoryId, territoryBoundaries);
      }

      return [BTGId, {
        boundaryTerritoryGroupId: BTGId,
        territoryAssignments,
        boundaryAssignments,
      }];
    }));
  });

export const useBoundaryTerritoryAssignments = () => useSelector(boundaryTerritoryAssignmentsSelector);
